import { ReactElement, useEffect, useState } from "react";
import { Menu, MenuProps } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

import "./menu.styles.scss";
import customersImage from "../../assets/customers.svg";
import { useSelector } from "react-redux";
import { StarFilled, StarOutlined } from "@ant-design/icons";
import SidebarService from "../../layout/sidebar/sidebar.service";
import toast from "react-hot-toast";
import FavouriteMenu from "../../models/favourite-menu.model";
import { ReactSVG } from "react-svg";
import clsx from "clsx";

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: "group"
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
  } as MenuItem;
}

const MenuComponent: React.FC = (): ReactElement => {
  const sidebarService = new SidebarService();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: any) => state.user);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const collapsed = useSelector((state: any) => state.collapseSidebar);
  const [activeKey, setActiveKey] = useState(location.pathname);

  useEffect(() => {
    if (
      location?.pathname?.includes("customers-list") ||
      location?.pathname?.includes("waiting-approvals") ||
      location?.pathname?.includes("online-trade-access") ||
      location?.pathname?.includes("/home/customer-profile") ||
      location?.pathname?.includes("/home/business-account") ||
      location?.pathname?.includes("/account-details") ||
      location?.pathname?.includes("/kyc-versions") ||
      location?.pathname?.includes("/document") ||
      location?.pathname?.includes("/customers/upload-customers") ||
      location?.pathname?.includes("/customers/view") ||
      location?.pathname?.includes("/home/create-customer") ||
      location?.pathname?.includes("/customers/upload") ||
      location?.pathname?.includes("/home/new-kyc")
    ) {
      if (
        location?.pathname?.includes("/home/customer-profile") ||
        location?.pathname?.includes("/home/business-account") ||
        location?.pathname?.includes("/account-details") ||
        location?.pathname?.includes("/kyc-versions") ||
        location?.pathname?.includes("/document") ||
        location?.pathname?.includes("/customers/review-form") ||
        location?.pathname?.includes("/customers/upload-customers") ||
        location?.pathname?.includes("/customers/view") ||
        location?.pathname?.includes("/home/create-customer") ||
        location?.pathname?.includes("/customers/upload") ||
        location?.pathname?.includes("/home/new-kyc")
      ) {
        setActiveKey("/home/customers-list");
      }
      setOpenKeys(["/customers"]);
    } else if (
      location?.pathname?.includes("countries") ||
      location?.pathname?.includes("portfolio/types") ||
      location?.pathname?.includes("portfolio/edit-portfolio")
    ) {
      if (location?.pathname?.includes("portfolio/edit-portfolio")) {
        setActiveKey("/portfolio/types");
      }
      setOpenKeys(["/master-data"]);
    } else if (
      location?.pathname?.includes("closure-requests") ||
      location?.pathname?.includes("portfolio") ||
      location?.pathname?.includes("deposit") ||
      location?.pathname?.includes("withdrawal") ||
      location?.pathname?.includes("cash-transfer") ||
      location?.pathname?.includes("subscription") ||
      location?.pathname?.includes("redemptions") ||
      location?.pathname?.includes("auto-invest") ||
      location?.pathname?.includes("queries")
    ) {
      if (location?.pathname?.includes("/portfolio/take-action")) {
        setActiveKey("/portfolio");
      }
      if (location?.pathname?.includes("/deposit/take-action")) {
        setActiveKey("/deposit");
      }
      if (location?.pathname?.includes("/redemptions/take-action")) {
        setActiveKey("/redemptions");
      }
      if (location?.pathname?.includes("/withdrawal/take-action")) {
        setActiveKey("/withdrawal");
      }
      if (location?.pathname?.includes("/cash-transfer/take-action")) {
        setActiveKey("/cash-transfer");
      }
      if (location?.pathname?.includes("/subscription/take-action")) {
        setActiveKey("/subscription");
      }
      setOpenKeys(["/requests"]);
    } else if (
      location?.pathname?.includes("funds") ||
      location?.pathname?.includes("news") ||
      location?.pathname?.includes("rating") ||
      location?.pathname?.includes("banks") ||
      location?.pathname?.includes("email-templates") ||
      location?.pathname?.includes("paci-logs") ||
      location?.pathname?.includes("slider-screens") ||
      location?.pathname?.includes("cards")
    ) {
      if (location?.pathname?.includes("/funds/edit-fund")) {
        setActiveKey("/funds");
      }
      setOpenKeys(["/Configuration"]);
    }
  }, []);

  const handleOpenChange = (keys: any): void => {
    const latestOpenKey = keys.find((key: any) => !openKeys.includes(key));
    if (latestOpenKey) {
      setOpenKeys([latestOpenKey]);
    } else {
      setOpenKeys([]);
    }
  };

  const handleMenuClick = (event: any): void => {
    navigate(event.key);
    setActiveKey(event.key);
  };

  const addToFavourite = async (
    event: React.MouseEvent<HTMLElement>,
    name: string,
    url: string
  ): Promise<void> => {
    event.stopPropagation();

    const response: boolean = await sidebarService.addToFavouriteList(
      name,
      url
    );
    if (response) {
      toast.success("The page added successfully to your favorite list!");
    }
  };

  const removeFromFavourite = async (
    event: React.MouseEvent<HTMLElement>,
    itemId: number
  ): Promise<void> => {
    event.stopPropagation();
    const response: boolean = await sidebarService.removeFromFavouriteList(
      itemId
    );
    if (response) {
      toast.success("The page removed successfully from your favorite list!");
    }
  };

  const isContainItemInFavouriteList = (url: string): FavouriteMenu => {
    const favouriteItem = user?.favoriteList?.find(
      (item: FavouriteMenu) => item.url === url
    );

    return favouriteItem;
  };

  const getItems = (): MenuItem[] => {
    const items: MenuItem[] = [
      getItem(
        <div className="flex flex-grow justify-between items-center">
          <p>Dashboard</p>
          {isContainItemInFavouriteList("/home") ? (
            <StarFilled
              className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
              onClick={(event) =>
                removeFromFavourite(
                  event,
                  isContainItemInFavouriteList("/home")?.id
                )
              }
            />
          ) : (
            <StarOutlined
              className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
              onClick={(event: React.MouseEvent<HTMLElement>) =>
                addToFavourite(event, "Dashboard", "/home")
              }
            />
          )}
        </div>,
        "/home",
        activeKey === "/home" || activeKey === "/" ? (
          <ReactSVG src={"/images/active-dashboard.svg"} />
        ) : (
          <ReactSVG src={"/images/dashboard.svg"} />
        )
      ),
    ];

    if (
      user?.permissions?.includes("view_customers") ||
      user?.permissions?.includes("waiting_for_approval") ||
      user?.permissions?.includes("view_online_trade_access")
    ) {
      items.splice(
        1,
        0,
        getItem(
          "Customers",
          "/customers",
          <img src={customersImage} alt="Kamco-customers" />,
          [
            user?.permissions?.includes("view_customers") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Customers list
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/home/customers-list"
                        ? "text-blue-500 font-semibold"
                        : ""
                    )}
                  >
                    Customers list
                  </p>
                  {isContainItemInFavouriteList("/home/customers-list") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/home/customers-list")
                            ?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(
                          event,
                          "Customers list",
                          "/home/customers-list"
                        )
                      }
                    />
                  )}
                </div>
              ),
              "/home/customers-list",
              activeKey === "/home/customers-list" ? (
                <ReactSVG src="/images/active-customers-list.svg" />
              ) : (
                <ReactSVG src="/images/customers-list.svg" />
              )
            ),
            user?.permissions?.includes("waiting_for_approval") &&
            user?.kycReviewGroup &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Waiting for approval
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p>Waiting for approval</p>
                  {isContainItemInFavouriteList(
                    "/customers/waiting-approvals"
                  ) ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList(
                            "/customers/waiting-approvals"
                          )?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(
                          event,
                          "Waiting for approval",
                          "/customers/waiting-approvals"
                        )
                      }
                    />
                  )}
                </div>
              ),
              "/customers/waiting-approvals",
              activeKey === "/customers/waiting-approvals" ? (
                <ReactSVG src="/images/active-waiting-approval.svg" />
              ) : (
                <ReactSVG src="/images/waiting-approval.svg" />
              )
            ),
            user?.permissions?.includes("enable_online_trading_port") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Online trade access
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p>Online trade access</p>
                  {isContainItemInFavouriteList(
                    "/customers/online-trade-access"
                  ) ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList(
                            "/customers/online-trade-access"
                          )?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(
                          event,
                          "Waiting for approval",
                          "/customers/online-trade-access"
                        )
                      }
                    />
                  )}
                </div>
              ),
              "/customers/online-trade-access",
              activeKey === "/customers/online-trade-access" ? (
                <ReactSVG src="/images/active-waiting-approval.svg" />
              ) : (
                <ReactSVG src="/images/waiting-approval.svg" />
              )
            ),
          ]
        )
      );
    }

    if (
      user?.permissions?.includes("portfolio_types") ||
      user?.permissions?.includes("countries")
    ) {
      items.splice(
        2,
        0,
        getItem(
          "Master data",
          "/master-data",
          <img src="/images/master-data.svg" alt="Kamco-date" />,
          [
            user?.permissions?.includes("countries") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">Countries</span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p>Countries</p>
                  {isContainItemInFavouriteList("/countries") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/countries")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Countries", "/countries")
                      }
                    />
                  )}
                </div>
              ),
              "/countries",
              activeKey === "/countries" ? (
                <ReactSVG src="/images/active-countries.svg" />
              ) : (
                <ReactSVG src="/images/countries.svg" />
              )
            ),
            user?.permissions?.includes("portfolio_types") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Portfolio types
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/portfolio/types"
                        ? "text-blue-500 font-semibold "
                        : ""
                    )}
                  >
                    Portfolio types
                  </p>
                  {isContainItemInFavouriteList("/portfolio/types") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/portfolio/types")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Types", "/portfolio/types")
                      }
                    />
                  )}
                </div>
              ),
              "/portfolio/types",
              activeKey === "/portfolio/types" ? (
                <ReactSVG src="/images/active-portfolio-types.svg" />
              ) : (
                <ReactSVG src="/images/portfolio-types.svg" />
              )
            ),
          ]
        )
      );
    }

    if (
      user?.permissions?.includes("request_account_closure") ||
      user?.permissions?.includes("request_portfolios") ||
      user?.permissions?.includes("request_deposit") ||
      user?.permissions?.includes("request_redemption") ||
      user?.permissions?.includes("request_withdrawal") ||
      user?.permissions?.includes("request_subscription") ||
      user?.permissions?.includes("request_cash_transfer") ||
      user?.permissions?.includes("request_queries")
    ) {
      items.splice(
        3,
        0,
        getItem(
          "Client requests",
          "/requests",
          <img src="/images/client-requests.svg" alt="Kamco-portfolio" />,
          [
            user?.permissions?.includes("request_portfolios") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Portfolios
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/portfolio"
                        ? "text-blue-500  font-semibold"
                        : ""
                    )}
                  >
                    Portfolios
                  </p>
                  {isContainItemInFavouriteList("/portfolio") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/portfolio")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Portfolios", "/portfolio")
                      }
                    />
                  )}
                </div>
              ),
              "/portfolio",
              activeKey === "/portfolio" ? (
                <ReactSVG src="/images/active-portfolio-types.svg" />
              ) : (
                <ReactSVG src="/images/portfolio-types.svg" />
              )
            ),
            user?.permissions?.includes("request_deposit") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">Deposit</span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/deposit"
                        ? "text-blue-500 font-semibold "
                        : ""
                    )}
                  >
                    Deposits
                  </p>
                  {isContainItemInFavouriteList("/deposit") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/deposit")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Desposit", "/deposit")
                      }
                    />
                  )}
                </div>
              ),
              "/deposit",
              activeKey === "/deposit" ? (
                <ReactSVG src="/images/active-deposit.svg" />
              ) : (
                <ReactSVG src="/images/deposit.svg" />
              )
            ),
            user?.permissions?.includes("request_redemption") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Fund redemptions
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/redemptions"
                        ? "text-blue-500 font-semibold "
                        : ""
                    )}
                  >
                    Fund redemptions
                  </p>
                  {isContainItemInFavouriteList("/redemption") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/redemption")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(
                          event,
                          "Fund redemptions",
                          "/redemption"
                        )
                      }
                    />
                  )}
                </div>
              ),
              "/redemptions",
              activeKey === "/redemptions" ? (
                <ReactSVG src="/images/active-fund-redemptions.svg" />
              ) : (
                <ReactSVG src="/images/fund-redemptions.svg" />
              )
            ),
            user?.permissions?.includes("request_subscription") &&
            getItem(
              collapsed ? (
                <span className=" font-normal ">Fund subscriptions</span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/subscription"
                        ? "text-blue-500 font-semibold "
                        : ""
                    )}
                  >
                    Fund subscriptions
                  </p>
                  {isContainItemInFavouriteList("/subscription") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/subscription")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(
                          event,
                          "Fund subscription",
                          "/subscription"
                        )
                      }
                    />
                  )}
                </div>
              ),
              "/subscription",
              activeKey === "/subscription" ? (
                <ReactSVG src="/images/active-fund-subscription.svg" />
              ) : (
                <ReactSVG src="/images/fund-subscription.svg" />
              )
            ),
            ((user?.permissions?.includes("request_withdrawal") &&
              user?.withdrawGroup) ||
              user?.permissions?.includes("request_withdrawal_WM")) &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Withdrawals
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/withdrawal"
                        ? "text-blue-500 font-semibold "
                        : ""
                    )}
                  >
                    Withdrawals
                  </p>
                  {isContainItemInFavouriteList("/withdrawal") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/withdrawal")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Withdrawal", "/withdrawal")
                      }
                    />
                  )}
                </div>
              ),
              "/withdrawal",
              activeKey === "/withdrawal" ? (
                <ReactSVG src="/images/active-withdrawal.svg" />
              ) : (
                <ReactSVG src="/images/withdrawal.svg" />
              )
            ),
            ((user?.permissions?.includes("request_withdrawal") &&
              user?.withdrawGroup) ||
              user?.permissions?.includes("request_withdrawal_WM")) &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Cash transfer
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/cash-transfer"
                        ? "text-blue-500 font-semibold "
                        : ""
                    )}
                  >
                    Cash transfer
                  </p>
                  {isContainItemInFavouriteList("/cash-transfer") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/cash-transfer")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(
                          event,
                          "Cash transfer",
                          "/cash-transfer"
                        )
                      }
                    />
                  )}
                </div>
              ),
              "/cash-transfer",
              activeKey === "/cash-transfer" ? (
                <ReactSVG src="/images/active-withdrawal.svg" />
              ) : (
                <ReactSVG src="/images/withdrawal.svg" />
              )
            ),
            user?.permissions?.includes("auto_invest") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Auto invest
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/auto-invest"
                        ? "text-blue-500 font-semibold "
                        : ""
                    )}
                  >
                    Auto invest
                  </p>
                  {isContainItemInFavouriteList("/auto-invest") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/auto-invest")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "AutoInvest", "/auto-invest")
                      }
                    />
                  )}
                </div>
              ),
              "/auto-invest",
              activeKey === "/auto-invest" ? (
                <ReactSVG src="/images/auto-invest-active.svg" />
              ) : (
                <ReactSVG src="/images/auto-invest-inactive.svg" />
              )
            ),
            user?.permissions?.includes("request_account_closure") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Closure requests
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p>Closure requests</p>
                  {isContainItemInFavouriteList("/home/closure-requests") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList(
                            "/home/closure-requests"
                          )?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(
                          event,
                          "Closure requests",
                          "/home/closure-requests"
                        )
                      }
                    />
                  )}
                </div>
              ),
              "/home/closure-requests",
              activeKey === "/home/closure-requests" ? (
                <ReactSVG src="/images/active-closure-request.svg" />
              ) : (
                <ReactSVG src="/images/closure-request.svg" />
              )
            ),

            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">Queries</span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p>Queries</p>
                  {isContainItemInFavouriteList("/queries") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/queries")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Queries", "/queries")
                      }
                    />
                  )}
                </div>
              ),
              "/queries",
              activeKey === "/queries" ? (
                <ReactSVG src="/images/queries-active-icon.svg" />
              ) : (
                <ReactSVG src="/images/queries-Inactive-icon.svg" />
              )
            ),
          ]
        )
      );
    }

    if (
      user?.permissions?.includes("funds_list") ||
      user?.permissions?.includes("news") ||
      user?.permissions?.includes("rating") ||
      user?.permissions?.includes("banks") ||
      user?.permissions?.includes("email_templates") ||
      user?.permissions?.includes("paci_logs") ||
      user?.permissions?.includes("splash_screens") ||
      user?.permissions?.includes("cards")
    ) {
      items.splice(
        4,
        0,
        getItem(
          "Configuration",
          "/Configuration",
          <img src="/images/configurations.svg" alt="Kamco-configuration" />,
          [
            user?.permissions?.includes("funds_list") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">Fund list</span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p
                    className={clsx(
                      activeKey === "/funds"
                        ? "text-blue-500 font-semibold"
                        : ""
                    )}
                  >
                    Funds list
                  </p>
                  {isContainItemInFavouriteList("/funds") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/funds")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Funds", "/funds")
                      }
                    />
                  )}
                </div>
              ),
              "/funds",
              activeKey === "/funds" ? (
                <ReactSVG src="/images/active-fund-list.svg" />
              ) : (
                <ReactSVG src="/images/fund-list.svg" />
              )
            ),
            // user?.permissions?.includes("news") &&
            //   getItem(
            //     collapsed ? (
            //       <span className=" font-normal text-black-500">News</span>
            //     ) : (
            //       <div className="flex flex-grow justify-between items-center">
            //         <p>News</p>
            //         {isContainItemInFavouriteList("/news") ? (
            //           <StarFilled
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event) =>
            //               removeFromFavourite(
            //                 event,
            //                 isContainItemInFavouriteList("/news")?.id
            //               )
            //             }
            //           />
            //         ) : (
            //           <StarOutlined
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event: React.MouseEvent<HTMLElement>) =>
            //               addToFavourite(event, "News", "/news")
            //             }
            //           />
            //         )}
            //       </div>
            //     ),
            //     "/news",
            //     activeKey === "/news" ? (
            //       <ReactSVG src="/images/active-news.svg" />
            //     ) : (
            //       <ReactSVG src="/images/news.svg" />
            //     )
            //   ),
            // user?.permissions?.includes("rating") &&
            //   getItem(
            //     collapsed ? (
            //       <span className=" font-normal text-black-500">Rating</span>
            //     ) : (
            //       <div className="flex flex-grow justify-between items-center">
            //         <p>Rating</p>
            //         {isContainItemInFavouriteList("/rating") ? (
            //           <StarFilled
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event) =>
            //               removeFromFavourite(
            //                 event,
            //                 isContainItemInFavouriteList("/rating")?.id
            //               )
            //             }
            //           />
            //         ) : (
            //           <StarOutlined
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event: React.MouseEvent<HTMLElement>) =>
            //               addToFavourite(event, "Rating", "/rating")
            //             }
            //           />
            //         )}
            //       </div>
            //     ),
            //     "/rating",
            //     activeKey === "/rating" ? (
            //       <ReactSVG src="/images/active-rating.svg" />
            //     ) : (
            //       <ReactSVG src="/images/rating.svg" />
            //     )
            //   ),
            // user?.permissions?.includes("banks") &&
            //   getItem(
            //     collapsed ? (
            //       <span className=" font-normal text-black-500">Banks</span>
            //     ) : (
            //       <div className="flex flex-grow justify-between items-center">
            //         <p>Banks</p>
            //         {isContainItemInFavouriteList("/banks") ? (
            //           <StarFilled
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event) =>
            //               removeFromFavourite(
            //                 event,
            //                 isContainItemInFavouriteList("/banks")?.id
            //               )
            //             }
            //           />
            //         ) : (
            //           <StarOutlined
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event: React.MouseEvent<HTMLElement>) =>
            //               addToFavourite(event, "Banks", "/banks")
            //             }
            //           />
            //         )}
            //       </div>
            //     ),
            //     "/banks",
            //     activeKey === "/banks" ? (
            //       <ReactSVG src="/images/active-banks.svg" />
            //     ) : (
            //       <ReactSVG src="/images/banks.svg" />
            //     )
            //   ),
            // user?.permissions?.includes("email_templates") &&
            //   getItem(
            //     collapsed ? (
            //       <span className=" font-normal text-black-500">
            //         Email templates
            //       </span>
            //     ) : (
            //       <div className="flex flex-grow justify-between items-center">
            //         <p>Email templates</p>
            //         {isContainItemInFavouriteList("/emil-templates") ? (
            //           <StarFilled
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event) =>
            //               removeFromFavourite(
            //                 event,
            //                 isContainItemInFavouriteList("/emil-templates")?.id
            //               )
            //             }
            //           />
            //         ) : (
            //           <StarOutlined
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event: React.MouseEvent<HTMLElement>) =>
            //               addToFavourite(
            //                 event,
            //                 "Emil templates",
            //                 "/emil-templates"
            //               )
            //             }
            //           />
            //         )}
            //       </div>
            //     ),
            //     "/email-templates",
            //     activeKey === "/email-templates" ? (
            //       <ReactSVG src="/images/active-email-templates.svg" />
            //     ) : (
            //       <ReactSVG src="/images/email-templates.svg" />
            //     )
            //   ),
            // user?.permissions?.includes("paci_logs") &&
            //   getItem(
            //     collapsed ? (
            //       <span className=" font-normal text-black-500">PACI logs</span>
            //     ) : (
            //       <div className="flex flex-grow justify-between items-center">
            //         <p>PACI logs</p>
            //         {isContainItemInFavouriteList("/paci-logs") ? (
            //           <StarFilled
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event) =>
            //               removeFromFavourite(
            //                 event,
            //                 isContainItemInFavouriteList("/paci-logs")?.id
            //               )
            //             }
            //           />
            //         ) : (
            //           <StarOutlined
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event: React.MouseEvent<HTMLElement>) =>
            //               addToFavourite(event, "PACI logs", "/paci-logs")
            //             }
            //           />
            //         )}
            //       </div>
            //     ),
            //     "/paci-logs",
            //     activeKey === "/paci-logs" ? (
            //       <ReactSVG src="/images/active-paci-logs.svg" />
            //     ) : (
            //       <ReactSVG src="/images/paci-logs.svg" />
            //     )
            //   ),
            // user?.permissions?.includes("splash_screens") &&
            //   getItem(
            //     collapsed ? (
            //       <span className=" font-normal text-black-500">
            //         Splash screens
            //       </span>
            //     ) : (
            //       <div className="flex flex-grow justify-between items-center">
            //         <p>Splash screens</p>
            //         {isContainItemInFavouriteList("/slider-screens") ? (
            //           <StarFilled
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event) =>
            //               removeFromFavourite(
            //                 event,
            //                 isContainItemInFavouriteList("/slider-screens")?.id
            //               )
            //             }
            //           />
            //         ) : (
            //           <StarOutlined
            //             className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
            //             onClick={(event: React.MouseEvent<HTMLElement>) =>
            //               addToFavourite(
            //                 event,
            //                 "Splash screen",
            //                 "/slider-screens"
            //               )
            //             }
            //           />
            //         )}
            //       </div>
            //     ),
            //     "/slider-screens",
            //     activeKey === "/slider-screens" ? (
            //       <ReactSVG src="/images/active-splash-screens.svg" />
            //     ) : (
            //       <ReactSVG src="/images/splash-screens.svg" />
            //     )
            //   ),
            user?.permissions?.includes("cards") &&
            getItem(
              collapsed ? (
                <span className=" font-normal text-black-500">
                  Notification center
                </span>
              ) : (
                <div className="flex flex-grow justify-between items-center">
                  <p>Notification center</p>
                  {isContainItemInFavouriteList("/cards") ? (
                    <StarFilled
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event) =>
                        removeFromFavourite(
                          event,
                          isContainItemInFavouriteList("/cards")?.id
                        )
                      }
                    />
                  ) : (
                    <StarOutlined
                      className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        addToFavourite(event, "Cards", "/cards")
                      }
                    />
                  )}
                </div>
              ),
              "/cards",
              activeKey === "/cards" ? (
                <ReactSVG src="/images/active-cards.svg" />
              ) : (
                <ReactSVG src="/images/cards.svg" />
              )
            ),
          ]
        )
      );
    }

    if (
      user?.permissions?.includes("backend_users") ||
      user?.permissions?.includes("backend_roles")
    ) {
      items.splice(
        5,
        0,
        getItem(
          <div className="flex flex-grow justify-between items-center">
            <p>Backend Users</p>
            {isContainItemInFavouriteList("/backend-users") ? (
              <StarFilled
                className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                onClick={(event) =>
                  removeFromFavourite(
                    event,
                    isContainItemInFavouriteList("/backend-users")?.id
                  )
                }
              />
            ) : (
              <StarOutlined
                className="star bg-transparent text-blue-500 hover:fill-blue-500 hover:stroke-blue-500"
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  addToFavourite(event, "Backend users", "/backend-users")
                }
              />
            )}
          </div>,
          "/backend-users",
          activeKey === "/backend-users" ? (
            <ReactSVG src="/images/active-backend-users.svg" />
          ) : (
            <ReactSVG src="/images/backend-users.svg" />
          )
        )
      );
    }
    return items;
  };

  return (
    <div className="menu-container">
      <Menu
        openKeys={openKeys}
        selectedKeys={[location.pathname]}
        mode="inline"
        theme="dark"
        items={getItems()}
        inlineCollapsed={collapsed}
        expandIcon={(props: any) => {
          if (props.isOpen) {
            return (
              <img
                src={"/images/minus-square.svg"}
                alt="Kamco-customers"
                className="w-[20px] h-[20px]"
              />
            );
          } else {
            return (
              <ReactSVG
                src={"/images/add-square.svg"}
                className="w-[20px] h-[20px] add-icon"
              />
            );
          }
        }}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
      />
    </div>
  );
};

export default MenuComponent;
